import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BlockType, SectionElementType } from '@common/entities';
import { BlockFieldMap, setActiveBlockFields } from 'state/slices/activeBlockFieldSlice';
import { RootState } from 'state/store';

import { Section } from 'types/page';
import { getBlockFormFieldPrefix, getFieldMappingForBlockType } from 'utils/block-field-map';

/**
 * List of block types that are dynamic (use field arrays or change at runtime).
 * These blocks will continue to register themselves individually.
 */
const DYNAMIC_BLOCK_TYPES = new Set<BlockType>([
  BlockType.Vehicle,
  BlockType.Pet,
  BlockType.Asset,
  BlockType.Income,
  BlockType.Coapplicant,
  BlockType.Guarantor,
  BlockType.AddressCurrent,
  BlockType.AddressPrevious,
  BlockType.Expense,
]);

/**
 * Determines if a block type requires dynamic registration.
 * Dynamic blocks use field arrays or have runtime-changing instances.
 */
const isDynamicBlock = (blockType: BlockType): boolean => DYNAMIC_BLOCK_TYPES.has(blockType);

/**
 * Registers all static block fields for the current page at once.
 * Static blocks are those that don't use field arrays or change at runtime.
 *
 * This hook should be called at the page level (BlankPage, OptionsPage) to
 * centralize field registration and improve performance by reducing the number
 * of Redux dispatches.
 *
 * Dynamic blocks (Vehicles, Pets, Income sub-types, etc.) continue to register
 * themselves individually using useRegisterBlockForDisplayLogic.
 */
export function useRegisterStaticBlockForDisplayLogic(sections: Section[]) {
  const dispatch = useDispatch();
  const { blockFieldMap } = useSelector((state: RootState) => state.blockFieldMap);

  useEffect(() => {
    const staticBlockFields: BlockFieldMap = [];

    sections.forEach((section) => {
      section.elements?.forEach((element: any) => {
        // Only process block elements
        if (element.elementType !== SectionElementType.Block) return;

        // Skip dynamic blocks - they register themselves
        if (isDynamicBlock(element.customType)) return;

        // Get field mappings for this static block with correct prefix
        const fields = getFieldMappingForBlockType({
          blockType: element.customType,
          configuration: element.configuration,
          formFieldPrefix: getBlockFormFieldPrefix(element.customType),
        });

        staticBlockFields.push(...fields);
      });
    });

    // Only dispatch if we have static fields to register
    if (staticBlockFields.length > 0) {
      dispatch(setActiveBlockFields([...blockFieldMap, ...staticBlockFields]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, dispatch]);
}
